"use client";

import Link from "next/link";
import { usePostHog } from "posthog-js/react";

/**
 * Tasteful, value-first conversion CTA used on content surfaces that have
 * buying intent but no clear next step (e.g. /companies, /insights).
 *
 * Fires one snake_case PostHog event on click (`event` prop), matching the
 * codebase convention (`landing_cta_clicked`, `pricing_cta_clicked`, …). The
 * `surface` + `locale` props are attached so the click can be funnelled by
 * origin page. Reuses the canonical client pattern from `landing-cta-link.tsx`
 * (`usePostHog()?.capture`) rather than introducing a new tracking path.
 */
interface ConversionCtaProps {
	/** Destination, already locale-prefixed (e.g. `/${locale}/employers`). */
	href: string;
	/** snake_case event name, e.g. "companies_employer_cta_clicked". */
	event: string;
	/** Origin surface for funnel grouping, e.g. "companies" | "insights". */
	surface: string;
	locale: string;
	className?: string;
	children: React.ReactNode;
}

export function ConversionCta({
	href,
	event,
	surface,
	locale,
	className,
	children,
}: ConversionCtaProps) {
	const posthog = usePostHog();

	return (
		<Link
			href={href}
			onClick={() =>
				posthog?.capture(event, {
					surface,
					locale,
				})
			}
			className={className}
		>
			{children}
		</Link>
	);
}
